import { Icon } from '@appd/uik';
import { every, get, has, isArray, isNull, isNumber, isString, omit, startsWith } from 'lodash';
import light from '../../../../../styles/theme/light.json';
import { ValidationResult } from '../../models';
import { extractColorVarFromString } from '../../utils';

const VALID_ICON_OPTIONS: Icon.Type[] = [
  ...Object.entries(Icon)
    .map(([category, icons]) => [category, Object.keys(icons).sort()] as const)
    .filter(([category]) => String(category) === 'Health')
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([category, names]) => names.map(name => `${category}.${name}` as Icon.Type)),
  'Status.Unknown',
];

const VALID_COLOR_OPTIONS = Object.keys(omit(light, ['particle']));

export const isValidStatusCardGroupData = (obj: any): ValidationResult => {
  let isValid = true;
  const errors: string[] = [];

  if (obj == undefined) {
    isValid = false;
    errors.push('Data is not defined');
    return { isValid, errors };
  }

  if (!isArray(obj)) {
    isValid = false;
    errors.push(`Data should be an array, but is of type ${typeof obj}`);
    return { isValid, errors };
  }

  isValid = every(obj, statusCardObj => {
    let isLabelValid = true;
    let isValueValid = true;
    let isColorValid = true;
    let isIconValid = true;

    if (get(statusCardObj, 'label') && !isString(statusCardObj.label)) {
      isLabelValid = false;
      errors.push(`Label should be a string, but is of type ${typeof statusCardObj.label}`);
    }

    if (!has(statusCardObj, 'value')) {
      isValueValid = false;
      errors.push("'value' property should be defined");
    }

    if (get(statusCardObj, 'value') && !(isNumber(statusCardObj.value) || isNull(statusCardObj.value))) {
      isValueValid = false;
      errors.push(`Value should be a number or null, but is of type ${typeof statusCardObj.value}`);
    }

    // Check if color is defined when using circle
    if (get(statusCardObj, 'icon') === 'dashify.circle') {
      if (get(statusCardObj, 'color') == null) {
        isColorValid = false;
        errors.push("'color' property should be defined");
      }

      if (get(statusCardObj, 'color')) {
        if (!isString(statusCardObj.color)) {
          isColorValid = false;
          errors.push(`Color should be a string, but is of type ${typeof statusCardObj.color}`);
        } else {
          const colorVar = extractColorVarFromString(statusCardObj.color);
          if (colorVar) {
            // Check if color is a valid Magnetic color
            if (!VALID_COLOR_OPTIONS.includes(colorVar)) {
              isColorValid = false;
              errors.push(`Color '${colorVar}' is not supported`);
            }
          } else if (
            startsWith(statusCardObj.color, '#') &&
            !/(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(statusCardObj.color)
          ) {
            // Check if color is a valid hex color
            isColorValid = false;
            errors.push(`Color '${statusCardObj.color}' is not a valid hexcode`);
          } else {
            isColorValid = false;
            errors.push(`Color '${statusCardObj.color}' is invalid`);
          }
        }
      }
    } else {
      // Check if icon is defined when not using circle
      if (get(statusCardObj, 'icon') == null) {
        isIconValid = false;
        errors.push("'icon' property should be defined");
      } else if (!isString(statusCardObj.icon) || !VALID_ICON_OPTIONS.includes(statusCardObj.icon)) {
        // Check if icon is supported
        isIconValid = false;
        errors.push(`Icon '${statusCardObj.icon}' is not supported`);
      }
    }
    return isLabelValid && isValueValid && isColorValid && isIconValid;
  });

  if (errors.length > 0) {
    console.error('Error with status cards data: ', errors);
  }

  return { isValid, errors };
};
